import {
  GoogleAuthProvider,
  OAuthProvider,
  createUserWithEmailAndPassword,
  getRedirectResult,
  onAuthStateChanged,
  reload,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInAnonymously,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  updateProfile,
  type User,
  type UserCredential,
} from "firebase/auth";
import { auth } from "../../lib/firebase/config";
import { isNativeApp } from "../../lib/platform/native";
import { PUBLIC_SITE_ORIGIN } from "../../lib/platform/publicUrl";
import { fetchFcmToken, getNotificationPermissionState } from "../../lib/firebase/messaging";
import { unregisterFcmToken } from "./notificationsTokenService";
import { trackOnce } from "../../lib/telemetry";
import { getLandingAttribution } from "../../features/landing/lib/landingAttribution";
import { clearAllPeopleSuggestionsCaches } from "../../features/home/utils/peopleSuggestionsCache";

/**
 * Flag de sessão (BUG-30): sobrevive à navegação do redirect OAuth (mesma
 * aba). Enquanto setada, a tela de login mantém os botões desabilitados —
 * fecha a janela de ~1-2s em que a tela re-renderiza (pós-redirect, no reload)
 * antes de `getRedirectResult` finalizar o login. Auto-cura:
 * `consumeOAuthRedirectResult` sempre limpa o flag (mesmo se o redirect foi
 * cancelado/abortado).
 *
 * ⚠️ O flag é POR ABA, não por provedor, e isso é proposital: só existe um
 * redirect em voo de cada vez, e `getRedirectResult` é agnóstico a provedor —
 * ele resolve qualquer fluxo que tenha voltado. Quando o Apple entrou (08/2026)
 * os símbolos perderam o "Google" do nome, porque um nome que cobre dois
 * provedores e cita um é a deriva que faz o próximo leitor achar que o Apple
 * não está protegido pelo guard.
 *
 * ⚠️ A CHAVE de sessionStorage continua a literal antiga de propósito: trocá-la
 * deixaria órfão o flag de quem estivesse com um redirect em voo no instante do
 * deploy — cosmético no código, um botão destravado cedo demais na tela.
 */
const OAUTH_REDIRECT_FLAG = "lura:google-redirect-pending";

export function isOAuthRedirectPending(): boolean {
  try {
    return sessionStorage.getItem(OAUTH_REDIRECT_FLAG) === "1";
  } catch {
    return false;
  }
}

function markOAuthRedirectPending(): void {
  try {
    sessionStorage.setItem(OAUTH_REDIRECT_FLAG, "1");
  } catch {
    /* sessionStorage indisponível — degrada pro comportamento anterior */
  }
}

function clearOAuthRedirectPending(): void {
  try {
    sessionStorage.removeItem(OAUTH_REDIRECT_FLAG);
  } catch {
    /* ignore */
  }
}

/**
 * PWA instalada, iframe, WebView Android ou qualquer browser mobile costumam
 * bloquear popup / COOP estrito (popup blocker do Safari iOS, polling de
 * `window.closed` bloqueado por COOP). Redirect completa o fluxo na próxima
 * carga com `consumeGoogleRedirectResult`.
 *
 * ⚠️ Esta função decide entre os dois caminhos WEB e só é alcançada na web: no
 * build nativo o `signInWithGoogle`/`signInWithApple` retorna antes, no
 * `isNativeApp()`. Isso importa porque os testes de UA abaixo diriam "redirect"
 * dentro do app — a WebView do Capacitor se declara iPhone/Android — e redirect
 * é exatamente o que o Google recusa lá (`disallowed_useragent`). Quem impede
 * isso é o early return, não uma guarda daqui: uma segunda checagem de
 * plataforma neste ponto seria inalcançável, e código que nenhum teste consegue
 * matar não é proteção, é peso morto que sugere uma segurança que não existe.
 */
function shouldUseRedirect(): boolean {
  if (typeof window === "undefined") return false;
  if (window !== window.parent) return true;
  const standalone =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(display-mode: standalone)").matches;
  if (standalone) return true;
  const ua = navigator.userAgent;
  if (/Android.+wv\)/i.test(ua)) return true;
  if (/iPhone|iPad|iPod|Android/i.test(ua)) return true;
  return false;
}

/**
 * Chame uma vez no boot do app (ex.: AuthProvider) para finalizar login OAuth
 * por redirect — Google ou Apple, indistintamente: `getRedirectResult` resolve
 * o fluxo que voltou, sem precisar saber qual provedor o iniciou.
 *
 * ⚠️ **No build nativo isto é um no-op, e tem de continuar sendo.** Não existe
 * redirect a consumir lá (nem Google nem Apple: os dois entram pelo SDK do
 * sistema e terminam em `signInWithCredential`), e o Auth nativo é construído
 * **sem `popupRedirectResolver`** justamente para tirar o iframe do authDomain
 * do caminho crítico do boot — ver `lib/firebase/config.ts`. Chamar
 * `getRedirectResult` nesse Auth lança `auth/argument-error` na hora, porque
 * `_withDefaultResolver` faz `_assert(auth._popupRedirectResolver, …)`.
 *
 * O early-return é o par da decisão lá; as duas peças são uma só.
 */
export async function consumeOAuthRedirectResult(): Promise<UserCredential | null> {
  if (isNativeApp()) return null;
  try {
    return await getRedirectResult(auth);
  } finally {
    // Sempre libera o guard do botão, mesmo se o redirect foi cancelado (result
    // null) ou lançou — senão o botão ficaria travado após uma tentativa falha.
    clearOAuthRedirectPending();
  }
}

/**
 * Provider do Google com o SELETOR DE CONTA sempre ligado (BUG-110).
 *
 * `signOut(auth)` encerra a sessão do LIVRA; a sessão do Google no navegador
 * (accounts.google.com) continua de pé, e o consentimento desta origem já foi
 * dado uma vez. Sem `prompt`, o endpoint de OAuth resolve sozinho: com UMA
 * conta ativa ele devolve aquela conta na hora, sem desenhar tela nenhuma —
 * então "sair e entrar de novo" reentrava na MESMA conta, sem escolha. É o que
 * parece cache e não é: é o Google respondendo o que ele considera óbvio.
 *
 * `prompt: "select_account"` força o seletor em toda tentativa. O custo é um
 * toque a mais pra quem tem conta única; o benefício é que trocar de conta
 * deixa de exigir deslogar do Google inteiro em outra aba.
 */
function buildGoogleProvider(): GoogleAuthProvider {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  return provider;
}

/**
 * Login Google DENTRO do app nativo (iOS/Android).
 *
 * ── Por que o caminho web não serve aqui ────────────────────────────────────
 * Nem popup nem redirect funcionam num app empacotado. O Google **recusa
 * OAuth em WebView** por política (`disallowed_useragent`, erro 403): a
 * WebView é controlada pelo app hospedeiro, que poderia ler a senha digitada,
 * então o endpoint de autorização se nega a desenhar a tela. Não é um bug a
 * contornar nem um user-agent a falsificar — falsificar o UA é o que faz a
 * conta ser sinalizada. O caminho suportado é entregar a autenticação a um
 * contexto que o app NÃO controla: Credential Manager / Google Sign-In no
 * Android, `ASWebAuthenticationSession` (Safari de verdade) no iOS. É o que o
 * SDK nativo faz, e o retorno chega pelo esquema de URL registrado no app.
 *
 * ── E por que o resultado ainda entra no SDK JS ─────────────────────────────
 * O app inteiro — Firestore, callables, `onAuthStateChanged`, rules — fala com
 * o SDK **JavaScript**. Autenticar só na camada nativa deixaria o JS deslogado
 * e nada funcionaria. Por isso o plugin roda com `skipNativeAuth: true`: ele
 * executa o fluxo do provedor, devolve a credencial e NÃO cria sessão nativa;
 * quem cria a sessão é o `signInWithCredential` do JS, exatamente como o
 * popup faria. Uma sessão só, no lugar de sempre.
 *
 * O import é dinâmico para que o bundle web não carregue o plugin: na web esta
 * função nunca é chamada.
 */
async function signInWithGoogleNative(): Promise<UserCredential> {
  const { FirebaseAuthentication } = await import(
    "@capacitor-firebase/authentication"
  );

  const result = await FirebaseAuthentication.signInWithGoogle();
  const idToken = result.credential?.idToken;
  if (!idToken) {
    // Sem idToken não há como criar a credencial do JS. Acontece quando o
    // `default_web_client_id` não foi preenchido — ou seja, quando o build
    // saiu sem `google-services.json` / `GoogleService-Info.plist`. Mensagem
    // explícita porque o sintoma cru ("nada acontece ao tocar em Entrar") não
    // aponta para o arquivo que falta.
    throw new Error(
      "Login Google nativo não devolveu idToken. Confira se o " +
        "google-services.json (Android) / GoogleService-Info.plist (iOS) do " +
        "app está no projeto nativo.",
    );
  }

  const credential = GoogleAuthProvider.credential(
    idToken,
    result.credential?.accessToken,
  );
  return signInWithCredential(auth, credential);
}

/**
 * Nativo: SDK do sistema. Web: popup por padrão, redirect em PWA / iframe /
 * WebView de rede social. Retorna `null` só quando iniciou um redirect.
 */
export const signInWithGoogle = async (): Promise<UserCredential | null> => {
  if (isNativeApp()) return signInWithGoogleNative();

  const provider = buildGoogleProvider();
  if (shouldUseRedirect()) {
    markOAuthRedirectPending();
    await signInWithRedirect(auth, provider);
    return null;
  }
  return signInWithPopup(auth, provider);
};

// ───────────────────────────── Sign in with Apple ────────────────────────────

/** Id do provedor Apple no Firebase. Literal em UM lugar só. */
const APPLE_PROVIDER_ID = "apple.com";

/**
 * Provider Apple. `email` e `name` são os dois únicos escopos que a Apple
 * oferece, e ambos só chegam na PRIMEIRA autorização de cada Apple ID —
 * reautorizar devolve o token sem eles, por desenho da Apple.
 *
 * Não há `prompt: "select_account"` a pedir aqui (o BUG-110 do Google): o
 * seletor da Apple é a própria sheet do sistema, que sempre lista as contas.
 */
function buildAppleProvider(): OAuthProvider {
  const provider = new OAuthProvider(APPLE_PROVIDER_ID);
  provider.addScope("email");
  provider.addScope("name");
  return provider;
}

/**
 * Sign in with Apple DENTRO do app nativo — mesmo desenho do Google nativo, e
 * pela mesma razão: `skipNativeAuth: true` faz o plugin rodar o fluxo do
 * sistema e devolver a credencial SEM criar sessão nativa; quem cria a sessão é
 * o `signInWithCredential` do SDK JS, que é com quem o app inteiro fala.
 *
 * No iOS o plugin usa `ASAuthorizationController`, a sheet nativa com Face ID —
 * não uma WebView. É o caminho mais estável do sistema e o que a HIG espera.
 *
 * ⚠️ `rawNonce`, NUNCA `nonce`. O plugin sorteia um nonce, manda o SHA-256 dele
 * para a Apple e devolve o valor CRU em `credential.nonce`. O Firebase refaz o
 * hash para comparar com a claim do id token: entregar o valor já hasheado (ou
 * omiti-lo quando existe) reprova a comparação e o login morre em
 * `auth/invalid-credential` — um erro que não diz nada sobre nonce nenhum.
 *
 * O valor é repassado VERBATIM, sem guarda de ausência: quando o plugin não
 * manda nonce, o token também não carrega a claim, e aí `undefined` é a
 * resposta certa. Inventar um erro para esse caso quebraria um fluxo que
 * funciona — a única coisa sem a qual não há credencial é o `idToken`.
 */
async function signInWithAppleNative(): Promise<UserCredential> {
  const { FirebaseAuthentication } = await import(
    "@capacitor-firebase/authentication"
  );

  const result = await FirebaseAuthentication.signInWithApple();
  const idToken = result.credential?.idToken;
  if (!idToken) {
    // Estado real por trás disso: capability "Sign in with Apple" ausente no
    // App ID / entitlement fora do build. O sintoma cru é a sheet abrir e o
    // login não acontecer — a mensagem tem de apontar o que falta.
    throw new Error(
      "Login Apple nativo não devolveu idToken. Confira a capability " +
        "'Sign in with Apple' no App ID e o entitlement " +
        "com.apple.developer.applesignin no build iOS.",
    );
  }

  const credential = buildAppleProvider().credential({
    idToken,
    rawNonce: result.credential?.nonce,
  });
  return signInWithCredential(auth, credential);
}

/**
 * Nativo: sheet do sistema. Web: popup por padrão, redirect em PWA / iframe /
 * navegador mobile. Retorna `null` só quando iniciou um redirect — mesmo
 * contrato do `signInWithGoogle`, para que a tela trate os dois igual.
 */
export const signInWithApple = async (): Promise<UserCredential | null> => {
  if (isNativeApp()) return signInWithAppleNative();

  const provider = buildAppleProvider();
  if (shouldUseRedirect()) {
    markOAuthRedirectPending();
    await signInWithRedirect(auth, provider);
    return null;
  }
  return signInWithPopup(auth, provider);
};

/**
 * Encerra a sessão nos DOIS lados — e no nativo isso não é zelo, é o BUG-110
 * de novo, por outra porta.
 *
 * `signOut(auth)` derruba só a sessão do SDK JS. No app nativo o provedor
 * mantém a própria credencial guardada (Credential Manager no Android,
 * `GIDSignIn` no iOS); sem limpá-la, o próximo "Entrar com Google" reentra na
 * MESMA conta sem desenhar seletor — que é exatamente o sintoma que o
 * `prompt: "select_account"` conserta na web, e que na web foi relatado como
 * "parece cacheado".
 *
 * A ordem é nativo → JS: se a limpeza nativa falhar, o `await` estoura e o
 * usuário continua logado com um estado coerente, em vez de ficar deslogado no
 * app e logado no provedor.
 */
/**
 * Desregistra o token FCM deste aparelho ANTES do signOut.
 *
 * ⚠️ A ORDEM É O FIX, não um detalhe de estilo. Depois de `signOut(auth)` não
 * existe mais `auth.uid`, e a escrita em `users/{uid}` é recusada pelas Rules —
 * o token ficaria no documento para sempre. Foi exatamente assim que o mesmo
 * iPhone acabou registrado em DUAS contas (medido em 24/08): push destinado a
 * uma delas cai no aparelho de quem estiver logado na outra.
 *
 * Gateado por permissão JÁ concedida de propósito: `fetchFcmToken` chama
 * `requestNotificationPermission()`, e sem esse guard SAIR DA CONTA poderia
 * abrir o diálogo do sistema pedindo notificação — no exato gesto de ir
 * embora. Sem permissão não existe token deste device, então o guard não
 * deixa nada para trás.
 *
 * Best-effort: falha aqui NUNCA pode impedir o logout.
 */
async function releaseFcmTokenBeforeSignOut(): Promise<void> {
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  try {
    if ((await getNotificationPermissionState()) !== "granted") return;
    const token = await fetchFcmToken();
    if (!token) return;
    await unregisterFcmToken(uid, token);
  } catch {
    // Sair da conta importa mais que limpar o token.
  }
}

export async function signOutEverywhere(): Promise<void> {
  await releaseFcmTokenBeforeSignOut();
  if (isNativeApp()) {
    const { FirebaseAuthentication } = await import(
      "@capacitor-firebase/authentication"
    );
    await FirebaseAuthentication.signOut();
  }
  await signOut(auth);
  // "Leitores parecidos": a lista guardada no aparelho é da conta que saiu
  // (people-suggestions.md §2, trava 3). Síncrono e sem rede.
  clearAllPeopleSuggestionsCaches();
}

export const signInWithEmail = async (
  email: string,
  password: string
): Promise<UserCredential> => {
  return signInWithEmailAndPassword(auth, email, password);
};

export const createUserWithEmail = async (
  email: string,
  password: string,
  // Opcional desde o redesign do Login (20/07/2026): o cadastro não pede mais
  // nome — quem coleta é o passo Perfil do onboarding. Sem nome aqui, o
  // `ensureUserProfile` deriva um a partir do prefixo do e-mail.
  displayName?: string
): Promise<UserCredential> => {
  const credential = await createUserWithEmailAndPassword(auth, email, password);
  // Fecho do funil de aquisição da landing (Fase 5 do redesign, 13/09/2026):
  // `cta_hero`/`cta_1`/`cta_2`/`cta_final`/`cta_sticky`/`cta_nav` levam todos
  // pra cá. Só o caminho de e-mail: distinguir conta NOVA de login por Google
  // exige `getAdditionalUserInfo` em vários call sites (popup, redirect,
  // nativo) que este commit não mexe — `createUserWithEmailAndPassword` só é
  // chamado no cadastro, então aqui não há ambiguidade nenhuma.
  trackOnce("signup_completed", { method: "email", ...getLandingAttribution() });
  if (displayName?.trim()) {
    await updateProfile(credential.user, { displayName: displayName.trim() });
  }
  // Verificação de identidade (30/08/2026). O envio NÃO bloqueia o cadastro:
  // quem manda no acesso é a rule `hasVerifiedEmail()` no fecho do onboarding,
  // e a tela de verificação sabe reenviar. Falha de rede aqui não pode
  // transformar "conta criada" em erro na cara de quem acabou de se cadastrar.
  try {
    await sendEmailVerification(credential.user, VERIFY_EMAIL_ACTION_SETTINGS);
  } catch {
    // silencioso de propósito — ver acima
  }
  return credential;
};

/**
 * Para onde o botão "Continuar" da página de ação do Firebase leva depois que
 * a pessoa clica no link do e-mail (BUG-264).
 *
 * Sem `url`, o link verifica e para numa página genérica do Google ("Your
 * email has been verified") sem caminho de volta — a pessoa tem que achar a
 * aba do app sozinha. Com ele, o "Continuar" devolve ao `/onboarding`, onde o
 * `EmailVerificationGate` re-checa a verificação ao ganhar foco.
 *
 * É SEMPRE o domínio público, nunca `window.location.origin`: no nativo a
 * origem é `capacitor://localhost` (não está — nem pode estar — nos Authorized
 * Domains, e o Firebase recusa o envio com `auth/unauthorized-continue-uri`),
 * e num preview channel mandar a pessoa de volta ao preview seria mandar para
 * um link que expira. `handleCodeInApp: false` porque a verificação é feita
 * pelo handler `/__/auth/action` do Hosting; o app não consome `oobCode`.
 */
const VERIFY_EMAIL_ACTION_SETTINGS = {
  url: `${PUBLIC_SITE_ORIGIN}/onboarding`,
  handleCodeInApp: false,
} as const;

/**
 * Reenvia o e-mail de verificação para a sessão atual.
 *
 * Existe porque a rule `hasVerifiedEmail()` barra o fecho do onboarding: sem
 * um caminho de reenvio, quem perdeu o e-mail (spam, digitou errado, fechou a
 * aba) ficaria preso numa tela sem saída — que é a classe de defeito que este
 * projeto já pagou caro (BUG-58, BUG-93).
 */
export const resendVerificationEmail = async (continuePath?: string): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error("Nenhuma sessão ativa.");
  // Quem pede fora do onboarding (ex.: a folha de "Criar a ficha", BUG-313) já
  // passou dele — o "Continuar" do e-mail o devolve ao app, não ao wizard.
  await sendEmailVerification(
    user,
    continuePath
      ? { ...VERIFY_EMAIL_ACTION_SETTINGS, url: `${PUBLIC_SITE_ORIGIN}${continuePath}` }
      : VERIFY_EMAIL_ACTION_SETTINGS,
  );
};

/**
 * Relê o usuário do servidor e devolve se o e-mail já está verificado.
 *
 * ⚠️ `auth.currentUser.emailVerified` é um espelho LOCAL do token e não muda
 * sozinho quando a pessoa clica no link em outra aba/aparelho — sem o
 * `reload()`, o botão "Já verifiquei" responderia "ainda não" para sempre.
 */
export const refreshEmailVerification = async (): Promise<boolean> => {
  const user = auth.currentUser;
  if (!user) return false;
  await reload(user);
  // O token em mãos ainda carrega `email_verified: false`; as Firestore Rules
  // leem o TOKEN, não o perfil. Forçar a renovação aqui é o que faz a regra
  // enxergar a verificação sem exigir logout/login.
  await user.getIdToken(true);
  return user.emailVerified === true;
};

/** E-mail da sessão atual, para telas fora do `AuthProvider` dizerem PARA ONDE vai o link. */
export const getCurrentUserEmail = (): string | null => auth.currentUser?.email ?? null;

export const sendPasswordReset = async (email: string): Promise<void> => {
  // Mesma classe do e-mail de verificação (BUG-264): depois de redefinir, o
  // "Continuar" volta ao login em vez de parar na página genérica do Google.
  return sendPasswordResetEmail(auth, email, {
    url: `${PUBLIC_SITE_ORIGIN}/login`,
    handleCodeInApp: false,
  });
};

export const onUserAuthStateChanged = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Sessão anônima do Firebase (F1.5 do Roadmap PRO, 11/09) — só existe para
 * satisfazer `isAuthed()` nas Firestore Rules de `users`/`user_stats`/
 * `user_books`/`connections` num visitante sem conta que abre um perfil
 * público. NÃO é "logar como convidado": quem chama isto NUNCA deve tratar o
 * `User` resultante como uma conta de verdade — `user.isAnonymous` é o campo
 * que distingue os dois, e `ProtectedRoute`/`RootEntry` já o tratam como
 * "sem sessão" de propósito (ver `docs/product/roadmap-pro.md` §F1.5).
 *
 * ⚠️ Requer Anonymous Sign-in HABILITADO no Console (Authentication → Sign-in
 * method) — não é algo que este código liga sozinho. Sem isso, rejeita com
 * `auth/admin-restricted-operation` e quem chama trata como falha de rede.
 */
export async function signInAsGuest(): Promise<User> {
  const credential = await signInAnonymously(auth);
  return credential.user;
}
